/**
 * Invitation preview loader
 *
 * Fetches public invite context via callable once per `signUpId` and stores it for
 * the welcome header therapist card.
 *
 * References:
 * - `api/fetchSignedUpPatientInviteContext.ts`
 */

import { useEffect } from 'react';
import { fetchSignedUpPatientInviteContext } from '../api/fetchSignedUpPatientInviteContext';
import { getSignUpId, useOnboardingStore } from '../store/onboardingStore';

/**
 * Loads invite preview for the current `signUpId`. Skips the callable when a settled result exists
 * for this id (`ready`, `not_found`, or `error`); refetches after {@link resetInviteContextCache}.
 */
export function useInviteContext(): void {
  const signUpId = useOnboardingStore(getSignUpId);
  const inviteContextResolvedForSignUpId = useOnboardingStore((s) => s.inviteContextResolvedForSignUpId);
  const beginInviteContextLoad = useOnboardingStore((s) => s.beginInviteContextLoad);
  const setInviteContextReady = useOnboardingStore((s) => s.setInviteContextReady);
  const setInviteContextNotFound = useOnboardingStore((s) => s.setInviteContextNotFound);
  const setInviteContextFailed = useOnboardingStore((s) => s.setInviteContextFailed);
  const clearPendingLinkUtmCode = useOnboardingStore((s) => s.clearPendingLinkUtmCode);

  useEffect(() => {
    if (!signUpId) {
      return;
    }
    if (inviteContextResolvedForSignUpId === signUpId) {
      return;
    }

    let cancelled = false;
    beginInviteContextLoad();

    const linkUtmCode = useOnboardingStore.getState().pendingLinkUtmCode;

    void fetchSignedUpPatientInviteContext(signUpId, linkUtmCode)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        if (useOnboardingStore.getState().signUpId !== signUpId) {
          return;
        }
        if (payload.status === 'error') {
          setInviteContextFailed(signUpId);
          return;
        }
        if (payload.notFound) {
          setInviteContextNotFound(signUpId);
          return;
        }
        clearPendingLinkUtmCode();
        setInviteContextReady(signUpId, payload.data);
      })
      .catch(() => {
        if (!cancelled && useOnboardingStore.getState().signUpId === signUpId) {
          setInviteContextFailed(signUpId);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    signUpId,
    inviteContextResolvedForSignUpId,
    beginInviteContextLoad,
    setInviteContextReady,
    setInviteContextNotFound,
    setInviteContextFailed,
    clearPendingLinkUtmCode,
  ]);
}
